// @ts-nocheck
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Phone, Plus, Users, Search, Radio, Sun, Moon, BellRing } from 'lucide-react';
import { AppRail, RAIL_FOCUS } from './AppRail';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { LiveCallPill } from './LiveCallPill';
import { CustomerSwitcher } from '@/components/dialer/CustomerSwitcher';
import PowerHourPanel from '@/components/dialer/PowerHourPanel';
import { TAB_META, TAB_TOUR_ATTR, primaryCallLabelKey } from '@/components/dialer/XDialSidebar';
import { resolveSettingsSection, settingsMenuRows } from '@/pages/dialer/settings/sections';
import { crmProviderShown } from '@/lib/hubspotUi';
import { useAuthStore } from '@/stores/authStore';
import { VIEW_NAV_LABEL, pathForView } from '@/layouts/dialerRoutes';
import { shortcutLabel } from '@/lib/platform';
import { api } from '@/api/api-client';

/** POST /api/notifications/read-all accepts at most this many ids (notifications.contract.ts, maxItems). */
const READ_ALL_MAX_IDS = 100;
import { showErrorToast } from '@/lib/errorToast';

/**
 * The dialer's rail (W1 shell, behind ui_refonte). Same functions as
 * XDialSidebar (client switcher, Power Hour, search, notifications, live
 * feed, theme, guided tour, settings sections), in the order of DESIGN.md
 * §5 and filtered by role; labels match the page titles (T-d5). XDialSidebar
 * keeps its structure for the flag-off path; both read the same nav labels,
 * renamed for every user in T-d5. Not yet here: drag-to-reorder the menu (a
 * saved order is applied).
 */
const GROUPS = [
  ['home', 'queue', 'followups', 'call-history', 'sms'],
  ['analytics', 'objections', 'crm'],
  ['client-portal', 'team', 'settings', 'billing'],
];
// Billing is an account decision: tenant admins only. DES-21 also wants it
// limited to SaaS tenants, which needs an organisation billing mode that does
// not exist yet (DESIGN.md §5).
const ADMIN_ONLY = new Set(['client-portal', 'team', 'billing']);

export function DialerRail({
  selectedView, onViewChange, stats, listPaused = false, theme, cycleTheme, collapsed, onCollapsedChange,
  onSearchOpen, onTourStart, onLiveFeedOpen, customers, selectedCustomerId, onCustomerChange, navTabOrder, collapsible = true,
}) {
  const { t } = useTranslation(['dialer', 'settings', 'common']);
  const isAdminLike = useAuthStore((st) => st.user?.isSuperAdmin === true || st.activeMembership?.role === 'tenant_admin');
  const clientActif = customers?.length ? ((selectedCustomerId && customers.find((c) => c.id === selectedCustomerId)) || customers[0]) : null;
  const crmConnecte = crmProviderShown(clientActif?.active_crm_provider) !== 'none';

  const queryClient = useQueryClient();
  const { data: notifications = [] } = useQuery({
    queryKey: ['caller-notifications'],
    queryFn: () => api.functions.invoke('getMyNotifications').then((r) => (r?.data ?? r)?.notifications ?? []),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  // Only the notifications on screen are acknowledged: one that arrived
  // after the list was fetched stays unread.
  const dismissNotifications = async () => {
    const ids = notifications.map((n) => n.id).filter(Boolean);
    try {
      // The route takes at most 100 ids per call (contract maxItems).
      for (let i = 0; i < ids.length; i += READ_ALL_MAX_IDS) await api.functions.invoke('markAllNotificationsRead', { ids: ids.slice(i, i + READ_ALL_MAX_IDS) });
    }
    // They stay unread; the user is told instead of nothing happening.
    catch (e) { showErrorToast(e); }
    finally { queryClient.invalidateQueries({ queryKey: ['caller-notifications'] }); }
  };

  const rank = (id) => {
    const i = navTabOrder?.indexOf(id) ?? -1;
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  // Tâches: the overdue callbacks (DESIGN.md §5, W4 B2-followups).
  const counts = { queue: stats?.queuedProspects, followups: stats?.overdueFollowupsCount };
  // Settings section shown in the page, read from ?section=; kept in state
  // and re-read on the page's xdial-settings-section event (as XDialSidebar).
  const readSection = () => { try { return new URLSearchParams(window.location.search).get('section'); } catch { return null; } };
  const [section, setSection] = useState<string | null>(readSection);
  useEffect(() => {
    const reread = () => setSection(readSection());
    reread();
    window.addEventListener('xdial-settings-section', reread);
    window.addEventListener('popstate', reread);
    return () => { window.removeEventListener('xdial-settings-section', reread); window.removeEventListener('popstate', reread); };
  }, [selectedView]);

  const groups = GROUPS.map((ids) => ids
    .filter((id) => (id !== 'crm' || crmConnecte) && (!ADMIN_ONLY.has(id) || isAdminLike))
    .sort((a, b) => rank(a) - rank(b))
    .map((id) => {
      const meta = TAB_META[id];
      return {
        id,
        label: t(VIEW_NAV_LABEL[id]),
        icon: id === 'team' ? Users : meta.icon,
        active: selectedView === id,
        badge: counts[id],
        tourId: TAB_TOUR_ATTR[id],
        onSelect: () => onViewChange(id),
        href: pathForView(id),
        children: id === 'settings' ? (
          <div className="flex flex-col" style={{ gap: 1, paddingLeft: 26, paddingBottom: 4 }}>
            {settingsMenuRows().map((row) => {
              if (row.kind === 'group') {
                return (
                  <p key={`group-${row.group}`} className="px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vf-text-3)' }}>
                    {t(`settings:groups.${row.group}`)}
                  </p>
                );
              }
              const ss = row.entry;
              const current = resolveSettingsSection(section) === ss.id;
              return (
                <button
                  key={ss.id}
                  type="button"
                  aria-current={current ? 'true' : undefined}
                  onClick={() => {
                    try {
                      const u = new URL(window.location.href);
                      u.searchParams.set('section', ss.id);
                      window.history.replaceState(window.history.state, '', u.toString()); // keep React Router's state (key, idx)
                      window.dispatchEvent(new Event('xdial-settings-section'));
                    } catch { /* URL unavailable */ }
                  }}
                  className={`w-full min-h-[32px] rounded-md px-2 py-1 text-left text-[12px] ${RAIL_FOCUS}`}
                  style={{ color: current ? 'var(--vf-text-1)' : 'var(--vf-text-2)', fontWeight: current ? 600 : 400, background: current ? 'var(--vf-surface-2)' : 'transparent' }}
                >
                  {t(`settings:sections.${ss.id}`)}
                </button>
              );
            })}
          </div>
        ) : undefined,
      };
    }));

  const isDark = theme === 'dark';
  return (
    <AppRail
      ariaLabel={t('dialer:nav.mainMenu')}
      collapsed={collapsed}
      onCollapsedChange={onCollapsedChange}
      collapsible={collapsible}
      onBrandClick={() => onViewChange('home')}
      primary={{ label: t(primaryCallLabelKey(listPaused)), icon: Phone, onSelect: () => onViewChange('queue') }}
      secondary={{ label: t('dialer:nav.newMeeting').replace(/^\+\s*/, ''), icon: Plus, onSelect: () => onViewChange('new-meeting') }}
      header={(open) => (
        <>
          <LiveCallPill open={open} />
          <CustomerSwitcher customers={customers} selectedCustomerId={selectedCustomerId} onCustomerChange={onCustomerChange} open={open} />
        </>
      )}
      groups={groups}
      footer={(open) => (
        <div className="flex flex-col" style={{ gap: 4, marginTop: 4 }}>
          {notifications.length > 0 && (
            // The messages are read first; only « Marquer comme lu » acknowledges them.
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className={`flex min-h-[32px] items-center rounded-md text-[12px] ${RAIL_FOCUS}`} style={{ gap: 8, padding: open ? '0 10px' : 0, justifyContent: open ? 'flex-start' : 'center', color: 'var(--vf-text-1)', background: 'var(--vf-accent-bg)' }} aria-label={open ? undefined : t('dialer:nav.notifications')}>
                  <BellRing size={14} aria-hidden />
                  {open && <span className="flex-1 text-left">{t('dialer:nav.notifications')}</span>}
                  <span className="tabular-nums text-[11px] font-semibold">{notifications.length}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent side="right" align="end" className="w-72 p-3" data-testid="rail-notifications">
                <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto text-[13px]" style={{ color: 'var(--vf-text-1)' }}>
                  {notifications.map((n, i) => (
                    <li key={n.id ?? i} className="rounded-md p-2" style={{ background: 'var(--vf-surface-2)' }}>
                      {n.title && <p className="font-semibold">{n.title}</p>}
                      <p>{n.message}</p>
                    </li>
                  ))}
                </ul>
                <button type="button" onClick={dismissNotifications} className={`mt-3 min-h-[32px] w-full rounded-md text-[12px] font-medium ${RAIL_FOCUS}`} style={{ border: '1px solid var(--vf-border)', color: 'var(--vf-text-1)' }}>
                  {t('dialer:nav.markNotificationsRead')}
                </button>
              </PopoverContent>
            </Popover>
          )}
          <PowerHourPanel collapsed={!open} />
          <button type="button" onClick={() => onSearchOpen?.()} className={`flex min-h-[32px] items-center rounded-md text-[12px] ${RAIL_FOCUS}`} style={{ gap: 8, padding: open ? '0 10px' : 0, justifyContent: open ? 'flex-start' : 'center', color: 'var(--vf-text-3)', background: 'var(--vf-surface-2)', border: '1px solid var(--vf-border)' }} aria-label={open ? undefined : t('common:actions.search')}>
            <Search size={14} aria-hidden />
            {open && <><span className="flex-1 text-left">{t('common:actions.search')}</span><span className="vf-font-mono text-[11px]">{shortcutLabel('K')}</span></>}
          </button>
          <div className="flex items-center" style={{ gap: 4, justifyContent: open ? 'flex-end' : 'center', flexDirection: open ? 'row' : 'column' }}>
            <button type="button" onClick={() => onLiveFeedOpen?.()} aria-label={t('dialer:nav.liveFeed')} title={t('dialer:nav.liveFeed')} className={`flex h-8 w-8 items-center justify-center rounded-md ${RAIL_FOCUS}`} style={{ border: '1px solid var(--vf-border)', color: 'var(--vf-text-3)' }}><Radio size={13} aria-hidden /></button>
            <button type="button" onClick={cycleTheme} aria-label={isDark ? t('dialer:xdialSidebar.lightMode') : t('dialer:xdialSidebar.darkMode')} title={isDark ? t('dialer:xdialSidebar.lightMode') : t('dialer:xdialSidebar.darkMode')} className={`flex h-8 w-8 items-center justify-center rounded-md ${RAIL_FOCUS}`} style={{ border: '1px solid var(--vf-border)', color: 'var(--vf-text-3)' }}>{isDark ? <Sun size={13} aria-hidden /> : <Moon size={13} aria-hidden />}</button>
            <button type="button" data-tour="tour-trigger" onClick={() => onTourStart?.()} aria-label={t('dialer:nav.tourGuide')} title={t('dialer:nav.tourGuide')} className={`flex h-8 w-8 items-center justify-center rounded-md text-[12px] font-bold ${RAIL_FOCUS}`} style={{ border: '1px solid var(--vf-border)', color: 'var(--vf-text-3)' }}>?</button>
          </div>
        </div>
      )}
    />
  );
}

export default DialerRail;
